import { NumberFormatter } from '@bimser/components';
import { FormItemTypes, IDictionary, isNullOrUndefined, MLHelper } from '@bimser/core';
import get from 'lodash/get';
import DataGrid2 from '../../..';
import { evaluateIt } from '../../../../../../../sagas/ruleValidation/rule';
import { IDataGridRow } from '../../../entities';
import { highestPrimary } from '../onInitNewRow';

export function newRow(this: DataGrid2, oldRow: Partial<IDataGridRow> = {}): IDataGridRow {
	let data: IDictionary<any> = oldRow.data || {};

	data = this.columns.reduce((result, column) => {
		if (!column.isPrimaryKey && column.dbType.type === 'decimal' && column.formula) {
			if (column.precision) {
				const evaluated = formulaEvaluate.call(this, column.formula, data);

				const numberFormatter = new NumberFormatter();
				numberFormatter.init(column.precision, false);
				const formatted = numberFormatter.format(evaluated, null) || '';

				const replaced =
					this.__form.currentLanguage.culture !== 'en-US'
						? formatted.replace('.', '').replace(',', '.')
						: formatted;

				const parsed = parseFloat(replaced);

				return { ...result, [column.name]: parsed };
			}

			return {
				...result,
				[column.name]: formulaEvaluate.call(this, column.formula, data)
			};
		}

		return result;
	}, data);

	const cellData = oldRow.cellData || {};
	const cells = this.columns.map((column, columnIndex) => {
		let text!: string;
		let value!: any;

		if (column.dbType.type === 'object') {
			switch (column.editType) {
				case 'comboBox':
					value = data[column.name]?.value || oldRow.data?.[column.name]?.value;
					text = data[column.name]?.text || oldRow.data?.[column.name]?.text;

					cellData[`__${column.name}`] =
						data[column.name]?.externalData || oldRow.data?.[column.name]?.externalData;
					break;
				case 'multiLanguageTextBox':
				default:
					value = data[column.name] || oldRow.data?.[column.name];
					text = data[column.name] || oldRow.data?.[column.name];
					break;
			}

			if (text) {
				if (typeof text === 'object') {
					text = MLHelper.getMLText(text);
				} else {
					text = text.toString();
				}
			}
		} else {
			text = data[column.name]?.toString() || oldRow.data?.[column.name]?.toString();
			value = data[column.name] || oldRow.data?.[column.name];

			if (column.isPrimaryKey && column.autoIncrement && (!text || !value)) {
				const incremented = highestPrimary(column.name, this.rows) + 1;

				text = incremented.toString();
				value = incremented;
			}
		}

		return {
			editType: column.editType,
			entityPath: (column.editControl as any)?.EntityPath,
			name: column.name,
			index: columnIndex,
			text,
			value
		};
	});

	return {
		rowId: oldRow.rowId || 0,
		index: !isNullOrUndefined(oldRow.index) ? oldRow.index : this.rows.length,
		selected: false,
		data,
		cellData,
		cells
	};
}

function formulaEvaluate(this: DataGrid2, formula: string, row: any): any {
	if (isNullOrUndefined(formula)) {
		return formula;
	}

	const controlRegex = /\[([\s\S]*?)\]/gm;
	const match = formula?.match(controlRegex);

	match?.forEach(source => {
		const rawPath = source.replace('[', '').replace(']', '').split('.');
		const sourceId = rawPath[0];
		const sourcePropPath = rawPath.slice(1, rawPath.length);

		let v: any;

		const sourceItem = (this.__form as any)[sourceId];

		if (sourceItem.controlType === FormItemTypes.EControlTypes.DataGrid) {
			if (sourcePropPath.indexOf('columns') === 0 && sourcePropPath.indexOf('value') === 2) {
				sourcePropPath.pop();
				const columnName = sourcePropPath.pop();
				v = row[columnName];
			} else if (sourcePropPath.indexOf('columns') === 0) {
				// This condition area is left for the purpose of preventing errors from previously developed formulas.
				const columnName = sourcePropPath.pop();
				v = row[columnName];
			} else {
				v = get(sourceItem, sourcePropPath);
			}
		} else {
			v = get(sourceItem, sourcePropPath);
		}

		if (isNullOrUndefined(v)) {
			v = '';
		}
		if (typeof v === 'string') {
			v = `'${v}'`;
		}
		if (typeof v === 'undefined') {
			v = '0';
		}

		formula = formula.replace(source, v);
	});
	return evaluateIt(formula);
}
